import { useRef, useState } from "react";
import { Upload, Loader2, X, ImageIcon } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Reusable image upload → Emergent object storage. Returns a public URL via onChange.
// Pass crop="square" to center-crop uploads to a uniform 1:1 aspect ratio (catalog images).
export default function ImageUpload({ value, onChange, label = "Image", rounded = false, crop = "", testid = "image-upload" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const pick = () => inputRef.current?.click();

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const url = crop === "square" ? "/uploads/image?crop=square" : "/uploads/image";
      const { data } = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "We couldn't upload that file. Please check it and try again.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div data-testid={testid}>
      {label && <label className="text-xs text-[#4A5D6B] mb-1 block">{label}</label>}
      <div className="flex items-center gap-3">
        <div className={`h-16 w-16 shrink-0 overflow-hidden bg-[#F4F1EB] border border-[#E5E0D8] grid place-items-center ${rounded ? "rounded-full" : "rounded-xl"}`}>
          {value ? <img src={value} alt="" className="h-full w-full object-cover" /> : <ImageIcon size={20} className="text-[#B9C4BD]" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex gap-2">
            <button type="button" onClick={pick} disabled={uploading} data-testid={`${testid}-btn`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs disabled:opacity-60">
              {uploading ? <Loader2 className="animate-spin" size={13} /> : <Upload size={13} />} {value ? "Replace" : "Upload"}
            </button>
            {value && <button type="button" onClick={() => onChange("")} data-testid={`${testid}-clear`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs !text-red-600 !border-red-300"><X size={13} /> Remove</button>}
          </div>
          <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="…or paste an image URL" data-testid={`${testid}-url`} className="eyw-input w-full text-xs mt-2" />
        </div>
      </div>
      <input ref={inputRef} type="file" accept="image/*" onChange={handle} className="hidden" />
    </div>
  );
}
